import { AutoAwesome as AutoAwesomeIcon } from '@mui/icons-material';
import { Chip, Tooltip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { differenceInDays, parseISO } from 'date-fns';

import type { FeatureChecker } from 'common/src/features/FeatureChecker';
import {
  DevFeatures,
  ProductFeatures,
} from 'common/src/types/enums/ProductFeatures.enum';
import { IAccount, IFacility, IMeter } from 'common/src/types/interfaces';

import addBooleanColumnProps from 'portal/src/components/common/tables/addBooleanColumnProps';
import addDateColumnProps from 'portal/src/components/common/tables/addDateColumnProps';
import { wrapHeader } from 'portal/src/components/common/tables/columnStyling';
import { masterSubMeterColumn } from 'portal/src/components/common/tables/masterSubColumns';

const buildMetersColumns = (featureChecker: FeatureChecker): GridColDef[] => {
  /**
   * Helper function to check if meter was created by internal user within 90 days
   * @param {Partial<IMeter>} meter - The meter object
   * @returns A boolean indicating if the meter is an automation meter
   */
  function isAutomationMeter(meter: Partial<IMeter>): boolean {
    if (!meter.createdBy || !meter.createdAt) return false;

    const isInternalUser = meter.createdBy.includes('@nimble-energy.com');

    const createdDate = parseISO(meter.createdAt);
    const daysSinceCreation = differenceInDays(new Date(), createdDate);
    const isWithin90Days = daysSinceCreation <= 90;

    return isInternalUser && isWithin90Days;
  }

  return [
    ...(featureChecker.devFeatureEnabled(DevFeatures.AutomationGenerate)
      ? [
          {
            field: 'isAutomation',
            headerName: 'Auto-Created',
            sortable: true,
            ...addBooleanColumnProps({
              includeTooltip: false,
              width: 80,
            }),
            renderCell: ({ row }) => {
              const isAutomation = isAutomationMeter(row);
              return isAutomation ? (
                <Tooltip title="Nimble Energy's automation process created this meter">
                  <Chip
                    icon={<AutoAwesomeIcon />}
                    label="Auto"
                    size="small"
                    color="primary"
                    variant="filled"
                    sx={{
                      fontSize: '0.75rem',
                    }}
                  />
                </Tooltip>
              ) : null;
            },
            valueGetter: (_, row) => isAutomationMeter(row),
          },
        ]
      : []),
    { field: 'name', headerName: 'Name', minWidth: 130, flex: 1 },
    {
      field: 'parentAccount',
      headerName: 'Parent Account',
      minWidth: 130,
      flex: 1,
      valueGetter: (value: IAccount) => value?.number ?? '',
      renderHeader: () => wrapHeader('Parent Account'),
    },
    {
      field: 'department',
      headerName: 'Department',
      minWidth: 130,
      flex: 1,
      valueGetter: (_, row: IMeter) => row.parentAccount?.department ?? '',
      renderHeader: () => wrapHeader('Department'),
    },
    {
      field: 'parentFacility',
      headerName: 'Parent Facility',
      minWidth: 100,
      flex: 1,
      valueGetter: (value: IFacility) => value?.name ?? '',
      renderHeader: () => wrapHeader('Parent Facility'),
    },
    {
      field: 'parentFacilityAddress',
      headerName: 'Parent Facility Address',
      minWidth: 120,
      flex: 1,
      valueGetter: (_, row: IMeter) => row.parentFacility?.address1 ?? '',
      renderHeader: () => wrapHeader('Parent Facility Address'),
    },
    {
      field: 'parentAccount.utility',
      headerName: 'Utility',
      width: 110,
      valueGetter: (_, row: IMeter) => row.parentAccount.utility.name,
    },
    {
      field: 'serviceType',
      headerName: 'Service Type',
      width: 100,
      renderHeader: () => wrapHeader('Service Type'),
    },
    {
      field: 'usageUnit',
      headerName: 'Usage Unit',
      width: 80,
      renderHeader: () => wrapHeader('Usage Unit'),
    },
    {
      field: 'demandUnit',
      headerName: 'Demand Unit',
      width: 80,
      renderHeader: () => wrapHeader('Demand Unit'),
    },
    {
      field: 'createdAt',
      ...addDateColumnProps('Created Date', 140),
    },
    {
      field: 'inactiveDate',
      ...addDateColumnProps('Inactive Date', 110),
    },
    {
      field: 'replacesMeter',
      headerName: 'Replaces Meter',
      minWidth: 100,
      valueGetter: (value: IMeter) => value?.name ?? '',
      renderHeader: () => wrapHeader('Replaces Meter'),
    },
    ...(featureChecker.productFeatureEnabled(ProductFeatures.allocations)
      ? [masterSubMeterColumn]
      : []),
    {
      field: 'needsReview',
      headerName: 'Needs Review',
      type: 'singleSelect',
      ...addBooleanColumnProps({
        displayLogic: { valueForYes: true, yesIsGreen: false },
        width: 115,
      }),
    },
    {
      field: 'isActive',
      headerName: 'Deactivated',
      ...addBooleanColumnProps({
        includeTooltip: true,
        displayLogic: { valueForYes: false, yesIsGreen: false },
        width: 100,
      }),
    },
  ];
};

export default buildMetersColumns;
